
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cog, Database, BarChart3, Cloud, ShoppingCart, FileText, LayoutGrid, CheckCircle, Target, Briefcase, Clock } from 'lucide-react';
import CtaSection from '../components/CtaSection';
import TestimonialsSection from '../components/TestimonialsSection';
import StatsSection from '../components/StatsSection';

const DigitalTransformationPage: React.FC = () => {
  // Sample stats data
  const stats = [
    {
      value: '100+',
      label: 'SMEs Transformed'
    },
    {
      value: '40%',
      label: 'Average Efficiency Increase'
    },
    {
      value: '25%',
      label: 'Average Cost Reduction'
    },
    {
      value: '30%',
      label: 'Increased Customer Satisfaction'
    }
  ];

  // Sample testimonials data
  const testimonials = [
    {
      content: "The digital transformation they implemented revolutionized our operations. Our manual processes are now automated, saving countless hours and significantly reducing errors.",
      author: 'Vijesh',
      position: 'Operations Director',
      company: 'RCPL.'
    },
    {
      content: "Their approach to digital transformation was tailored to our specific needs. They took the time to understand our business processes before recommending solutions, which made all the difference.",
      author: 'Dr Charu Naithani',
      position: 'Founder',
      company: 'Dentasmile'
    },
    {
      content: "Moving our operations to the cloud with their guidance has been a game-changer. We've seen a 35% reduction in IT costs and our team can now work from anywhere efficiently.",
      author: 'Vinayak Sarawagi',
      position: 'IT Manager',
      company: 'Squareboat'
    }
  ];

  return (
    <>

      {/* What We Do */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-900 to-indigo-600">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              What We Do
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-white">
              End-to-End Digital Transformation for SMEs
            </h2>
            <p className="md:text-lg text-white">
              We empower small and medium businesses with the tools, strategies, and technologies needed to thrive in the digital age.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <Cog size={28} />
              </div>
              <h3 className="text font-bold mb-3 text-gray-800">Business Process Automation</h3>
              <p className="text-gray-600 mb-6">
                Streamline workflows, reduce manual tasks, and improve efficiency with custom automation solutions tailored to your business processes.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <Database size={28} />
              </div>
              <h3 className="text font-bold mb-3 text-gray-800">ERP/CRM Implementation</h3>
              <p className="text-gray-600 mb-6">
                Implement enterprise resource planning and customer relationship management systems that integrate your business operations and improve customer insights.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <LayoutGrid size={28} />
              </div>
              <h3 className="text font-bold mb-3 text-gray-800">Website & App Development</h3>
              <p className="text-gray-600 mb-6">
                Establish or upgrade your digital presence with modern, responsive websites and custom applications that engage customers and showcase your services.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <Cloud size={28} />
              </div>
              <h3 className="text font-bold mb-3 text-gray-800">Cloud & Infrastructure Migration</h3>
              <p className="text-gray-600 mb-6">
                Move from on-premises systems to secure, scalable cloud infrastructure that reduces costs, improves accessibility, and enhances disaster recovery.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <BarChart3 size={28} />
              </div>
              <h3 className="text font-bold mb-3 text-gray-800">Data Analytics & Dashboards</h3>
              <p className="text-gray-600 mb-6">
                Transform raw data into actionable insights with custom analytics tools and real-time dashboards that inform better business decisions.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <ShoppingCart size={28} />
              </div>
              <h3 className="text font-bold mb-3 text-gray-800">E-commerce Enablement</h3>
              <p className="text-gray-600 mb-6">
                Expand your sales channels with e-commerce solutions that integrate with your existing systems and provide seamless customer experiences.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition duration-300 md:col-start-2 lg:col-auto">
              <div className="w-14 h-14 mb-6 rounded-lg flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-600">
                <FileText size={28} />
              </div>
              <h3 className="text font-bold mb-3 text-gray-800">Custom Software Solutions</h3>
              <p className="text-gray-600 mb-6">
                Address unique business challenges with tailor-made software solutions designed specifically for your industry and operational requirements.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-white mb-6">
              Whether you're going paperless, streamlining operations, or scaling digitally — we make it possible.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-lg text-blue-600 bg-white hover:bg-gray-50 font-medium transition duration-300"
            >
              Discuss Your Digital Transformation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <StatsSection
        stats={stats}
        sectionStyle="dark"
      />

      {/* Why SMEs Work With Us */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Why SMEs Work With Us
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              Digital Without the Overwhelm
            </h2>
            <p className="md:text-lg text-gray-600">
              We understand the unique challenges SMEs face when implementing new technologies. Our approach is designed to make digital transformation accessible and manageable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white">
                    <Target size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-4 text-gray-900">Deep Understanding of SME Needs</h3>
                  <p className="text-gray-600 mb-4">
                    We solve real business problems, not just tech puzzles. Our solutions are built with a thorough understanding of SME operations, constraints, and growth opportunities.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Industry-specific expertise</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Practical, business-focused solutions</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">SME-appropriate technology choices</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white">
                    <Briefcase size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-4 text-gray-900">Affordable, Scalable Solutions</h3>
                  <p className="text-gray-600 mb-4">
                    Solutions that grow with you — no overengineering. We design systems that start with what you need today but can scale as your business expands.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Flexible pricing models</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Modular implementation approach</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Future-proof architecture</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white">
                    <Clock size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-4 text-gray-900">Hands-On Partnership</h3>
                  <p className="text-gray-600 mb-4">
                    We guide you from strategy to execution and beyond. Our team works closely with yours to ensure a smooth transition and effective adoption of new technologies.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Dedicated project management</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Comprehensive staff training</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Ongoing support and mentorship</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white">
                    <BarChart3 size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-4 text-gray-900">Measurable Business Impact</h3>
                  <p className="text-gray-600 mb-4">
                    Track ROI, efficiency gains, and customer satisfaction. We implement solutions with clear metrics to demonstrate the value and impact on your business.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">ROI-focused implementation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Performance monitoring dashboards</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="mr-2 h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Regular impact assessments</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 md:py-24 bg-gray-50 px-4">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Our Approach
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              Your Business. Our Expertise. One Digital Journey.
            </h2>
            <p className="text-justify md:text-lg text-gray-600">
              We follow a proven methodology to ensure your digital transformation is smooth, effective, and aligned with your business goals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="relative p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="absolute -top-6 -left-6 w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xl">1</div>
              <h3 className="text-lg font-bold mb-4 text-gray-900 mt-4">Assessment & Strategy</h3>
              <p className="text-gray-600 mb-4">
                We begin by understanding your current state and designing a practical roadmap for your digital transformation journey.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-blue-600">
                    <CheckCircle size={16} className="text-blue-500" />
                  </div>
                  <span>Business process analysis</span>
                </li>
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-blue-600">
                    <CheckCircle size={16} className="text-blue-500" />
                  </div>
                  <span>Technology assessment</span>
                </li>
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-blue-600">
                    <CheckCircle size={16} className="text-blue-500" />
                  </div>
                  <span>Digital roadmap creation</span>
                </li>
              </ul>
            </div>

            <div className="relative p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="absolute -top-6 -left-6 w-12 h-12 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-xl">2</div>
              <h3 className="text-lg font-bold mb-4 text-gray-900 mt-4">Design & Implementation</h3>
              <p className="text-gray-600 mb-4">
                We build custom digital tools and workflows that fit your operations, focusing on practicality and user adoption.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-indigo-600">
                    <CheckCircle size={16} className="text-indigo-500" />
                  </div>
                  <span>Solution architecture</span>
                </li>
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-indigo-600">
                    <CheckCircle size={16} className="text-indigo-500" />
                  </div>
                  <span>Agile development</span>
                </li>
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-indigo-600">
                    <CheckCircle size={16} className="text-indigo-500" />
                  </div>
                  <span>Staged deployment</span>
                </li>
              </ul>
            </div>

            <div className="relative p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="absolute -top-6 -left-6 w-12 h-12 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-xl">3</div>
              <h3 className="text-lg font-bold mb-4 text-gray-900 mt-4">Training & Integration</h3>
              <p className="text-gray-600 mb-4">
                We ensure smooth adoption by your teams, with comprehensive training and support during the transition period.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-purple-600">
                    <CheckCircle size={16} className="text-purple-500" />
                  </div>
                  <span>User training programs</span>
                </li>
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-purple-600">
                    <CheckCircle size={16} className="text-purple-500" />
                  </div>
                  <span>Documentation and guides</span>
                </li>
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-purple-600">
                    <CheckCircle size={16} className="text-purple-500" />
                  </div>
                  <span>Change management support</span>
                </li>
              </ul>
            </div>

            <div className="relative p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="absolute -top-6 -left-6 w-12 h-12 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-xl">4</div>
              <h3 className="text-lg font-bold mb-4 text-gray-900 mt-4">Ongoing Support & Optimization</h3>
              <p className="text-gray-600 mb-4">
                We provide continuous improvement based on real-time business needs and evolving technology landscape.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-green-600">
                    <CheckCircle size={16} className="text-green-500" />
                  </div>
                  <span>Ongoing technical support</span>
                </li>
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-green-600">
                    <CheckCircle size={16} className="text-green-500" />
                  </div>
                  <span>Performance monitoring</span>
                </li>
                <li className="flex items-start">
                  <div className="mt-1 mr-3 text-green-600">
                    <CheckCircle size={16} className="text-green-500" />
                  </div>
                  <span>Regular system enhancements</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <TestimonialsSection testimonials={testimonials} />

      {/* Industries */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Industries We Serve
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              Digital Solutions for Every Sector
            </h2>
            <p className="md:text-lg text-gray-600">
              We've helped businesses across multiple industries transform their operations with tailored digital solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition duration-300">
              <h3 className="text-lg font-bold mb-3 text-gray-900">Retail & E-commerce</h3>
              <p className="text-gray-600 mb-4">
                Inventory management, point-of-sale systems, e-commerce platforms, customer analytics, and omnichannel experiences.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition duration-300">
              <h3 className="text-lg font-bold mb-3 text-gray-900">Manufacturing</h3>
              <p className="text-gray-600 mb-4">
                Production planning, inventory tracking, quality control, supply chain optimization, and equipment monitoring systems.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition duration-300">
              <h3 className="text-lg font-bold mb-3 text-gray-900">Professional Services</h3>
              <p className="text-gray-600 mb-4">
                Client management, project tracking, billing automation, resource allocation, and service delivery platforms.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition duration-300">
              <h3 className="text-lg font-bold mb-3 text-gray-900">Healthcare</h3>
              <p className="text-gray-600 mb-4">
                Patient management, appointment scheduling, electronic records, compliance solutions, and telehealth platforms.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition duration-300">
              <h3 className="text-lg font-bold mb-3 text-gray-900">Logistics & Transportation</h3>
              <p className="text-gray-600 mb-4">
                Route optimization, fleet management, shipment tracking, warehouse management, and delivery scheduling systems.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition duration-300">
              <h3 className="text-lg font-bold mb-3 text-gray-900">Education & Training</h3>
              <p className="text-gray-600 mb-4">
                Learning management systems, student information systems, online course platforms, and educational content delivery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Frequently Asked Questions
            </span>
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-gray-900">
              Common Questions About Digital Transformation
            </h2>
            <p className="md:text-lg text-gray-600">
              Find answers to questions businesses commonly ask about our digital transformation services.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    What size of SME do you work with?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    We typically serve businesses with 10 to 500 employees — across all sectors. Our solutions are designed to be accessible and effective for businesses at various stages of growth and with different levels of technical expertise.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    Do you offer industry-specific solutions?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    Yes. We tailor our tools to your industry—be it retail, logistics, manufacturing, or services. Our team has experience across multiple sectors, allowing us to understand your specific challenges and implement solutions that address your industry's unique requirements.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    How much does digital transformation cost?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    It varies based on your goals and systems. We offer flexible packages that deliver ROI quickly. Our approach is modular, allowing you to start with the most critical areas and expand over time. We provide transparent pricing and focus on solutions that will generate value and pay for themselves through increased efficiency and revenue.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    How long does digital transformation take?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    Digital transformation is a journey, not a one-time project. Initial implementations typically take 2-6 months depending on complexity, but we aim to deliver value in phases so you can see results quickly. We work with you to create a realistic timeline that balances speed with thorough implementation and proper adoption.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    Will my employees need special training?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    Yes, and we include comprehensive training as part of our service. User adoption is critical to successful digital transformation, so we provide documentation, hands-on training sessions, and ongoing support to ensure your team is comfortable and proficient with the new tools and processes.
                  </p>
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>

      <CtaSection
        title="Let's Future-Proof Your Business"
        subtitle="Start with a free digital audit and see where transformation can take you."
        buttonText="Request Your Free Audit"
        buttonLink="/contact"
        sectionStyle="gradient"
      />
    </>
  );
};

export default DigitalTransformationPage;
